import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Key,
  Mail,
  Building2,
  UserCog,
  Shield,
  ClipboardCheck,
  CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'case_manager',
    position: '',
    office_location: '',
    reports_to: '',
    m365_email: '',
    phone_number: '',
    is_active: true
  });

  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  const [togglingCaseloadSupervisor, setTogglingCaseloadSupervisor] = useState(null);
  const [togglingManageAllAppointments, setTogglingManageAllAppointments] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchOffices();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      const rolesData = response.data.roles || response.data || [];
      // Filter to only active roles
      setRoles(rolesData.filter(r => r.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      // Fallback to default roles
      setRoles([
        { name: 'system_admin', display_name: 'System Administrator', color: '#dc2626' },
        { name: 'executive', display_name: 'Executive', color: '#9333ea' },
        { name: 'operations_manager', display_name: 'Operations Manager', color: '#2563eb' },
        { name: 'case_manager', display_name: 'Case Manager', color: '#16a34a' },
        { name: 'intake_coordinator', display_name: 'Intake Coordinator', color: '#ea580c' },
        { name: 'compliance_officer', display_name: 'Compliance Officer', color: '#0891b2' },
        { name: 'support_staff', display_name: 'Support Staff', color: '#6b7280' }
      ]);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await axios.get(`${API}/admin/office-locations`);
      setOffices(response.data.offices || []);
    } catch (error) {
      console.error('Failed to fetch offices:', error);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'case_manager',
        position: user.position || '',
        office_location: user.office_location || '',
        reports_to: user.reports_to || '',
        m365_email: user.m365_email || '',
        phone_number: user.phone_number || '',
        is_active: user.is_active !== false
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'case_manager',
        position: '',
        office_location: '',
        reports_to: '',
        m365_email: '',
        phone_number: '',
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }
    
    setSaving(true);

    try {
      const payload = { ...formData };
      if (editingUser && !payload.password) {
        delete payload.password;
      }

      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, payload);
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/admin/users`, payload);
        toast.success('User created successfully');
      }
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/users/${deletingUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordUser) return;
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);

    try {
      await axios.put(`${API}/admin/users/${passwordUser.id}/password`, {
        password: passwordData.new_password
      });
      toast.success('Password reset successfully');
      setShowPasswordDialog(false);
      setPasswordUser(null);
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_active: !user.is_active
      });
      toast.success(`User ${!user.is_active ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleCaseloadReviewSupervisor = async (user) => {
    setTogglingCaseloadSupervisor(user.id);
    try {
      const newStatus = !user.caseload_review_supervisor;
      await axios.put(`${API}/admin/users/${user.id}/caseload-review-supervisor`, {
        enabled: newStatus
      });
      toast.success(`Caseload Review Supervisor ${newStatus ? 'enabled' : 'disabled'} for ${user.name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Caseload Review Supervisor permission');
    } finally {
      setTogglingCaseloadSupervisor(null);
    }
  };

  const handleToggleManageAllAppointments = async (user) => {
    setTogglingManageAllAppointments(user.id);
    try {
      const newStatus = !user.can_manage_all_appointments;
      await axios.put(`${API}/admin/users/${user.id}/manage-all-appointments`, {
        enabled: newStatus
      });
      toast.success(`Manage All Appointments ${newStatus ? 'enabled' : 'disabled'} for ${user.name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Manage All Appointments permission');
    } finally {
      setTogglingManageAllAppointments(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    if (role?.color) {
      return { backgroundColor: `${role.color}20`, color: role.color };
    }
    // Fallback colors
    switch(roleName) {
      case 'system_admin': return { backgroundColor: '#fecaca', color: '#dc2626' };
      case 'executive': return { backgroundColor: '#f3e8ff', color: '#9333ea' };
      case 'operations_manager': return { backgroundColor: '#dbeafe', color: '#2563eb' };
      case 'case_manager': return { backgroundColor: '#dcfce7', color: '#16a34a' };
      default: return { backgroundColor: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getRoleDisplayName = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    return role?.display_name || roleName;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="users-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-500">Manage user accounts and access permissions</p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Users ({users.length})</CardTitle>
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
              data-testid="user-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Office</TableHead>
                  <TableHead>CR Supervisor</TableHead>
                  <TableHead>Manage Appts</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge style={getRoleBadgeColor(user.role)}>
                        {getRoleDisplayName(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.position || '-'}</TableCell>
                    <TableCell>
                      {user.office_location && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          {user.office_location}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={user.caseload_review_supervisor || false}
                          onCheckedChange={() => handleToggleCaseloadReviewSupervisor(user)}
                          disabled={togglingCaseloadSupervisor === user.id}
                          data-testid={`cr-supervisor-toggle-${user.id}`}
                        />
                        {user.caseload_review_supervisor && (
                          <ClipboardCheck className="w-4 h-4 text-purple-500" title="Caseload Review Supervisor" />
                        )}
                        {togglingCaseloadSupervisor === user.id && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={user.can_manage_all_appointments || false}
                          onCheckedChange={() => handleToggleManageAllAppointments(user)}
                          disabled={togglingManageAllAppointments === user.id}
                          data-testid={`manage-appts-toggle-${user.id}`}
                        />
                        {user.can_manage_all_appointments && (
                          <CalendarDays className="w-4 h-4 text-blue-500" title="Manage All Appointments" />
                        )}
                        {togglingManageAllAppointments === user.id && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? "default" : "secondary"}>
                        {user.is_active ? (
                          <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setPasswordUser(user);
                            setShowPasswordDialog(true);
                          }}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleOpenDialog(user)}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setDeletingUser(user);
                            setShowDeleteDialog(true);
                          }}
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information and access' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  placeholder="Case Manager"
                  data-testid="user-position-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@helpcenter.org"
                required
                data-testid="user-email-input"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter password"
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Access Level *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({...formData, role: value})}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.name} value={role.name}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: role.color || '#6b7280' }}
                          />
                          <span>{role.display_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Link to="/admin/roles" className="text-xs text-primary hover:underline">
                  Manage roles & permissions
                </Link>
              </div>
              <div className="space-y-2">
                <Label htmlFor="office">Office Location</Label>
                <Select
                  value={formData.office_location || "none"}
                  onValueChange={(value) => setFormData({...formData, office_location: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="user-office-select">
                    <SelectValue placeholder="Select office" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No office assigned</SelectItem>
                    <SelectItem value="Nashville">Nashville</SelectItem>
                    <SelectItem value="Charlotte">Charlotte</SelectItem>
                    {offices.filter(o => o.is_active && !['Nashville', 'Charlotte'].includes(o.name)).map((office) => (
                      <SelectItem key={office.id} value={office.name}>{office.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reports_to">Reports To</Label>
              <Select
                value={formData.reports_to || "none"}
                onValueChange={(value) => setFormData({...formData, reports_to: value === "none" ? "" : value})}
              >
                <SelectTrigger data-testid="user-reports-to-select">
                  <SelectValue placeholder="Select supervisor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No supervisor</SelectItem>
                  {users.filter(u => u.id !== editingUser?.id && u.is_active).map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="m365_email">Microsoft 365 Email</Label>
              <Input
                id="m365_email"
                type="email"
                value={formData.m365_email}
                onChange={(e) => setFormData({...formData, m365_email: e.target.value})}
                placeholder="john@organization.onmicrosoft.com"
                data-testid="user-m365-email-input"
              />
              <p className="text-xs text-gray-500">Used for sending emails via Microsoft 365</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number (for RingOut Calls)</Label>
              <Input
                id="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                placeholder="+1 (615) 555-1234"
                data-testid="user-phone-input"
              />
              <p className="text-xs text-gray-500">Your phone will ring first when making outbound calls</p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="user-active-switch"
              />
              <Label>Active Account</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for {passwordUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={saving} data-testid="reset-password-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingUser?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
